// Lofi piano instruments (docs/AUDIO_DESIGN.md §12, style 'piano'): a felt piano synthesized live, and a brush kit.
// Piano, per note 3 oscillators + 2 gains + a low-pass (6 nodes, like the EP): two "strings" (PeriodicWaves: partials 1-2,
// and a .22x copy of partials 1-4 detuned 0.5-1.5 x detune ¢ away, so they beat slowly) and a third oscillator carrying
// partials 3..N stretched sharp by 12.5·B (inharmonicity B·n²/2 at n ≈ 5, B 4e-4 in the bass → 1e-4 in the treble)
// through its own gain (louder the harder the strike, dying faster than the fundamental), all through a per-note
// low-pass that closes as the note decays (felt: brightness falls with time, pitch and softness). Chords, the
// left hand and the melody share one pool of voices under ctx.cap (a voice already in release is stolen first, then the
// oldest above the left hand); one persistent noise gate gives every note-on its felt hammer thump.
import { clamp } from '../../engine/core.js';
import { mtof, noise } from '../dsp.js';
import { gain, filter, osc, panner, persistent } from '../instruments.js';

// Levels (tuning knobs for the mix balance against jazzhop's EP / bass / kit / lead).
export const PIANO_LEVELS = { note: 0.56, hammer: 1.6, lead: 1.05, left: 0.92, kit: 1.3 };

/* ---------------- partial tables: 8 register bands of 8 semitones, cached per AudioContext ---------------- */
// band b holds MIDI 32+8b .. 39+8b (b 0..7, clamped). Partials n = 1..N with N ≤ 10.5 kHz / top note (well under
// Nyquist/2) and ≤ 16/13/10/8/7/6/5/4 by band (felt: fewer partials up the keyboard). Amplitude: a1 = .7/.85/1 (bass
// strings have a weak fundamental), a_n = g·n^-p·(.35 + .65·|sin(π n/8.5)|) with p = 1.2 + .12b, g = 1.6 − .08b (the
// hammer strikes near 1/8.5 of the string, so partials 8-9 are nearly missing): in the middle (b 3) the 2nd partial sits
// at −9 dB, the 3rd −13, the 4th −16 before the felt low-pass. A holds partials 1-2, B is .22 of partials 1-4 (the beats
// swing ±2 dB, not a tremolo), S holds partials 3..N (stretched).
const WAVES = new WeakMap();
const NMAX = [16, 13, 10, 8, 7, 6, 5, 4];
function waves(ac, b) {
  let m = WAVES.get(ac);
  if (!m) WAVES.set(ac, (m = []));
  if (m[b]) return m[b];
  const N = Math.max(3, Math.min(NMAX[b], Math.floor(10500 / mtof(40 + 8 * b))));
  const p = 1.2 + 0.12 * b, g = 1.6 - 0.08 * b, a1 = b === 0 ? 0.7 : b === 1 ? 0.85 : 1;
  const amp = n => (n === 1 ? a1 : g * Math.pow(n, -p) * (0.35 + 0.65 * Math.abs(Math.sin(Math.PI * n / 8.5))));
  const make = fn => {
    const re = new Float32Array(N + 1), im = new Float32Array(N + 1);
    for (let n = 1; n <= N; n++) im[n] = fn(n);
    return ac.createPeriodicWave(re, im, { disableNormalization: true });
  };
  return (m[b] = { A: make(n => (n <= 2 ? amp(n) : 0)), B: make(n => (n <= 4 ? 0.22 * amp(n) : 0)), S: make(n => (n >= 3 ? amp(n) : 0)) });
}

/* ---------------- felt piano ---------------- */
const band = m => clamp(Math.floor((m - 32) / 8), 0, 7);
/** damper release τ: 60 ms in the treble → 160 ms at C2 (felt dampers take a moment on thick strings) */
const damp = m => 0.06 + 0.1 * clamp((64 - m) / 28, 0, 1);
/** level by register: the bass rings long and rich, so it is eased (×.77 at MIDI 34); the top octave is eased too */
const reg = m => (m < 48 ? 0.8 + 0.2 * (m - 36) / 12 : m > 76 ? Math.max(0.75, 1 - 0.015 * (m - 76)) : 1);

/**
 * createPiano(ac, out, send, plan, ctx) → { chord, stats, dispose, left: { note, dispose }, right: { note, dispose } }
 * plan.piano: { detune ¢, stretch, hammer, bright, roll } · plan.ep.lp: the felt low-pass. ctx: the style voices ctx.
 * chord(t, midis, vel, dur, strum, rel): the engine's ep events (voicing[0] is the left hand's root); left.note: the
 * `bass` events (the rest of the left hand); right.note: the melody (`lead` events).
 */
export function createPiano(ac, out, send, plan, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null, stepDur = 0.2 }) {
  const P = { detune: 1, stretch: 1, hammer: 1, bright: 1, roll: 0.2, ...plan.piano }, L = PIANO_LEVELS;
  // bus: 3 register panners (bass −.18 · middle 0 · treble +.18, the piano as heard from the bench) → soundboard body
  // (peaking 220 Hz +2 dB, Q .8) → felt low-pass (plan.ep.lp, Q .5) → dry, and a reverb send .28
  const pans = [-0.18, 0, 0.18].map(p => panner(ac, p)), sum = gain(ac, 1);
  const body = filter(ac, 'peaking', 220, 0.8), felt = filter(ac, 'lowpass', plan.ep.lp, 0.5), sendG = gain(ac, 0.28);
  body.gain.value = 2;
  for (const p of pans) p.connect(sum);
  sum.connect(body); body.connect(felt); felt.connect(out); felt.connect(sendG); sendG.connect(send);
  // hammer: looped white noise → BP at 2.5 f (400-2400 Hz, Q .7) → gate: τ 1.5 ms up, τ 14 ms down, 1.6·hammer·v², ×1.3 in
  // the bass → ×.6 at the top: the thump sits ~23 dB under the note's first 30 ms across the keyboard
  const hSrc = ac.createBufferSource(), hBP = filter(ac, 'bandpass', 900, 0.7), hG = gain(ac, 0);
  hSrc.buffer = noise(ac, 'white'); hSrc.loop = true;
  hSrc.connect(hBP); hBP.connect(hG); hG.connect(pans[1]);
  const bus = persistent(registry, tag, [hSrc], [...pans, sum, body, felt, sendG, hSrc, hBP, hG], t0, r() * 2);
  const hammer = (t, m, f, v) => {
    hBP.frequency.setValueAtTime(clamp(2.5 * f, 400, 2400), t);
    hG.gain.setTargetAtTime(L.hammer * P.hammer * v * v * clamp(0.7 + (70 - m) / 30, 0.6, 1.3), t, 0.0015); hG.gain.setTargetAtTime(0, t + 0.005, 0.014);
  };

  let voices = [], maxActive = 0, steals = 0, notes = 0;      // { v, m, kind: 'ch'|'lh'|'ld', start, off, end, rec }
  /** fade a voice from t (τ); `now` ends it for the cap and the log at t (a steal), else after 4τ */
  const release = (x, t, tau, now = false) => {
    registry.stop(x.v, t, tau);
    x.off = Math.min(x.off, t);
    x.end = x.start >= t ? x.start : Math.min(x.end, now ? t : t + 4 * tau);
    if (x.rec) x.rec[1] = x.end;
  };
  const oldest = xs => xs.reduce((a, x) => (!a || x.start < a.start ? x : a), null);

  function note(t, m, vel, dur, relTau, kind) {
    const f = mtof(m), v = clamp(vel, 0.05, 1), b = band(m), off = t + Math.max(0.05, dur);
    voices = voices.filter(x => x.end > t);
    for (const x of voices) if (x.m === m && x.start < t && x.off > t) release(x, t, 0.03);     // the same key struck again
    if (voices.length >= cap) {                     // steal: a voice in release first, then the oldest above the left hand
      const x = oldest(voices.filter(y => y.off <= t)) || oldest(voices.filter(y => y.m >= 50)) || oldest(voices);
      release(x, t, 0.01, true);
      voices = voices.filter(y => y !== x);
      steals++;
    }
    const W = waves(ac, b);
    const dA = (r() * 2 - 1) * P.detune, dB = dA + (0.5 + r()) * P.detune * (r() < 0.5 ? -1 : 1);
    const eps = 12.5 * (4e-4 - 3e-4 * b / 7) * P.stretch;      // 8.6¢ sharp in the bass → 2.2¢ at the top (× stretch)
    const o1 = osc(ac, 'sine', f * Math.pow(2, dA / 1200)), o2 = osc(ac, 'sine', f * Math.pow(2, dB / 1200)), o3 = osc(ac, 'sine', f * (1 + eps));
    o1.setPeriodicWave(W.A); o2.setPeriodicWave(W.B); o3.setPeriodicWave(W.S);
    // felt low-pass: opens to (300 + f·(.8 + 7v²))·bright Hz at the strike (700 Hz-5.2 kHz, and ≥ 1.1 × where it closes:
    // C4 opens to 850 Hz at v .3, 1.8 kHz at v .85), closes to 350 + 1.6 f (420 Hz-2.6 kHz) with τ .3·(262/f)^.3 s
    const fc1 = clamp(350 + 1.6 * f, 420, 2600), fc0 = clamp((300 + f * (0.8 + 7 * v * v)) * P.bright, Math.max(700, 1.1 * fc1), 5200);
    const lpf = filter(ac, 'lowpass', fc0, 0.6), amp = gain(ac, 0), hiG = gain(ac, 0);
    o1.connect(lpf); o2.connect(lpf); o3.connect(hiG); hiG.connect(lpf); lpf.connect(amp); amp.connect(pans[m < 52 ? 0 : m < 72 ? 1 : 2]);
    // upper partials: .35 + 1.3v² (+8.8 dB from v .3 to v .85), falling to .35 of that with τ .35·(262/f)^.4 s (the prompt
    // sound is bright, the aftersound nearly pure)
    const H = 0.35 + 1.3 * v * v;
    hiG.gain.setValueAtTime(H, t); hiG.gain.setTargetAtTime(0.35 * H, t + 0.01, 0.35 * Math.pow(262 / f, 0.4));
    // amplitude: felt attack 4-12 ms (softer = slower, +2 ms in the bass) to .56·v^1.5·reg; prompt sound down to s1 of the
    // peak (.52 in the bass → .3 in the treble) with τ1 = .2·(262/f)^.35 s; then the aftersound from 2.5 τ1 on, towards 0
    // with τ2 = 3.4·(262/f)^.55 s (0.9-8 s); note-off (the pedal lifting at the chord change) = the damper, τ 60-160 ms
    // (or the event's release when longer: the final chord's .9 s)
    const atk = 0.004 + 0.006 * (1 - v) + (m < 48 ? 0.002 : 0), A = L.note * Math.pow(v, 1.5) * reg(m);
    const t1 = 0.2 * Math.pow(262 / f, 0.35), s1 = clamp(0.48 - 0.07 * Math.log2(f / 130), 0.3, 0.52);
    const t2 = clamp(3.4 * Math.pow(262 / f, 0.55), 0.9, 8), tr = relTau >= 0.3 ? relTau : damp(m);
    const tA = t + atk, tB = tA + 2.5 * t1;
    amp.gain.setValueAtTime(0, t); amp.gain.linearRampToValueAtTime(A, tA);
    amp.gain.setTargetAtTime(A * s1, tA, t1);
    if (off > tB) amp.gain.setTargetAtTime(0, tB, t2);
    amp.gain.setTargetAtTime(0, off, tr);
    lpf.frequency.setValueAtTime(fc0, t); lpf.frequency.setTargetAtTime(fc1, tA, 0.3 * Math.pow(262 / f, 0.3));
    const stop = Math.min(off + 6 * tr, tB + 6 * t2), end = Math.min(off + 4 * tr, tB + 4.5 * t2);
    for (const o of [o1, o2, o3]) { o.start(t); o.stop(stop); }
    hammer(t, m, f, v);
    const x = { v: registry.add({ src: [o1, o2, o3], all: [o1, o2, o3, hiG, lpf, amp], start: t, end: stop, tag, out: amp }), m, kind, start: t, off, end, rec: log ? [t, end] : null };
    if (x.rec) log.push(x.rec);
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }

  const api = {
    /** a chord (or the notes a comping cell picks), bottom to top over `strum` s; a block of 3+ notes is rolled over 60-110 ms
     *  with p plan.piano.roll. The top voice +10 % velocity, the left hand's root (below MIDI 50) −10 %. */
    chord(t, midis, vel, dur, strum = 0.02, rel = 0.08) {
      const n = midis.length, off = t + dur;
      // the harmony moves on before earlier chord notes end (the engine's push on step 14): lift the pedal on them now
      for (const x of voices) if (x.kind === 'ch' && x.start < t - 0.03 && x.off > t + 0.05 && x.off < off - 2 * stepDur) release(x, t + 0.012, damp(x.m));
      const roll = n >= 3 && r() < P.roll ? 0.06 + r() * 0.05 : strum;
      midis.forEach((m, k) => {
        const dt = n > 1 ? (k * roll) / (n - 1) : 0;
        const vk = (n > 1 && k === n - 1 ? Math.min(1, vel * 1.1) : m < 50 ? vel * 0.9 : vel) * (0.96 + 0.08 * r());
        note(t + dt, m, vk, dur - dt, rel, 'ch');
      });
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.dispose(t); },
    /** the rest of the left hand (fifths, octaves, tenths, re-strikes): a long final note gets a .6 s release */
    left: { note: (t, m, vel, dur) => note(t, m, vel * L.left, dur, dur > 2.5 ? 0.6 : 0, 'lh'), dispose: t => api.dispose(t) },
    /** the right hand's melody: notes ring on a little under the pedal (×1.4 + 0.15 s, ≤ 2.4 s) */
    right: { note: (t, m, vel, dur) => note(t, m, vel * L.lead, Math.min(2.4, dur * 1.4 + 0.15), 0, 'ld'), dispose: t => api.dispose(t) },
  };
  return api;
}

/* ---------------- brush kit: persistent voices sharing one noise source ---------------- */
/** kit: { kickF0, kickF1, kickDecay, snareDecay, snareBP, hatHP, hatClosed, lp }. Lanes: kick, snare (brush), hat (brush
 *  tap; open = a swish), rim (cross-stick) and shaker (a brush sweep), so the engine never needs createPerc. */
export function createBrushKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime, r = Math.random }) {
  const L = PIANO_LEVELS.kit, nodes = [];
  const N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  bus.connect(out);
  // kick: sine kickF0 → kickF1 (τ 35 ms), soft attack (τ 2.5 ms), round decay (kickDecay); felt beater: noise → BP 800 Hz
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = N(gain(ac, 0)), kBP = N(filter(ac, 'bandpass', 800, 0.8)), kThump = N(gain(ac, 0));
  kOsc.connect(kAmp); kAmp.connect(bus); src.connect(kBP); kBP.connect(kThump); kThump.connect(bus);
  // brush snare: noise → BP snareBP (Q .5), onset τ 3 ms, a long sweep (snareDecay); a little triangle body 190 → 165 Hz
  const sSend = N(gain(ac, 0.22)), sBP = N(filter(ac, 'bandpass', kit.snareBP, 0.5)), sNoise = N(gain(ac, 0));
  const sOsc = N(osc(ac, 'triangle', 165)), sTone = N(gain(ac, 0));
  src.connect(sBP); sBP.connect(sNoise); sNoise.connect(bus); sNoise.connect(sSend);
  sOsc.connect(sTone); sTone.connect(bus); sTone.connect(sSend); sSend.connect(send);
  // brush tap (hat lane): noise → HP hatHP (Q .5), pan +.25, send .1; closed τ hatClosed, open = a 0.2 s swish (same gain)
  const hHP = N(filter(ac, 'highpass', kit.hatHP, 0.5)), hAmp = N(gain(ac, 0)), hPan = N(panner(ac, 0.25)), hSend = N(gain(ac, 0.1));
  src.connect(hHP); hHP.connect(hAmp); hAmp.connect(hPan); hPan.connect(bus); hPan.connect(hSend); hSend.connect(send);
  // cross-stick (rim lane): sine 520 (.6) + triangle 1400 (.35) → BP 900 Hz (Q .7), τ 10 ms, pan −.2, send .3
  const r1 = N(osc(ac, 'sine', 520)), r2 = N(osc(ac, 'triangle', 1400)), rg1 = N(gain(ac, 0.6)), rg2 = N(gain(ac, 0.35));
  const rBP = N(filter(ac, 'bandpass', 900, 0.7)), rAmp = N(gain(ac, 0)), rPan = N(panner(ac, -0.2)), rSend = N(gain(ac, 0.3));
  r1.connect(rg1); r2.connect(rg2); rg1.connect(rBP); rg2.connect(rBP); rBP.connect(rAmp); rAmp.connect(rPan);
  rPan.connect(bus); rPan.connect(rSend); rSend.connect(send);
  // brush sweep (shaker lane): noise → BP 3 kHz (Q .45), swells in (τ 12 ms) and out (τ 70 ms), pan +.1
  const xBP = N(filter(ac, 'bandpass', 3000, 0.45)), xAmp = N(gain(ac, 0)), xPan = N(panner(ac, 0.1));
  src.connect(xBP); xBP.connect(xAmp); xAmp.connect(xPan); xPan.connect(bus);
  const p = persistent(registry, tag, [src, kOsc, sOsc, r1, r2], nodes, t0, r() * 3);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, 0.035);
      hit(kAmp.gain, 0.48 * v * L, t, 0.0025, 0.01, kit.kickDecay);
      hit(kThump.gain, 0.1 * v * L, t, 0.001, 0.003, 0.008);
    },
    snare(t, v, ghost = false) {
      sOsc.frequency.setValueAtTime(190, t); sOsc.frequency.setTargetAtTime(165, t, 0.03);
      hit(sTone.gain, 0.08 * v * L, t, 0.002, 0.006, 0.04);
      hit(sNoise.gain, 0.5 * v * L, t, 0.003, 0.012, ghost ? 0.05 : kit.snareDecay);
    },
    hat(t, v, open = false) { hit(hAmp.gain, (open ? 0.16 : 0.12) * v * L, t, open ? 0.006 : 0.0015, 0.004, open ? 0.2 : kit.hatClosed); },
    rim(t, v) { hit(rAmp.gain, 0.3 * v * L, t, 0.0008, 0.002, 0.01); },
    shaker(t, v) { hit(xAmp.gain, 0.14 * v * L, t, 0.012, 0.02, 0.07); },
    dispose(t) { p.dispose(t); },
  };
}
